import React, { useEffect, useState } from "react";
import {
  Alert,
  Button,
  Card,
  DatePicker,
  Empty,
  Form,
  Input,
  InputNumber,
  Modal,
  Select,
  Space,
  Spin,
  Switch,
  Table,
  Tag,
  Typography,
} from "antd";
import { PlusOutlined, EditOutlined, GiftOutlined } from "@ant-design/icons";
import dayjs, { Dayjs } from "dayjs";
import { useTranslation } from "react-i18next";

import { API_URL } from "../../shared/utils/Util";
import { getFetchT, fetchT, centsToDollar, dollarToCents } from "../../shared/utils/TypedUtil";
import { useAntdTheme, useNotification } from "../../shared/hooks";
import {
  PromoCode,
  CreatePromoCodeRequest,
  UpdatePromoCodeRequest,
  DiscountType,
  ItemSchemaOption,
} from "./types";

const { Text } = Typography;

// localTimezoneLabel returns the user's browser-local timezone abbreviation
// (e.g. "PST", "PDT") for display next to expire-time labels. Falls back to
// the IANA timezone name (e.g. "America/Los_Angeles") and finally to "Local"
// if neither can be resolved.
function localTimezoneLabel(): string {
  try {
    const parts = new Intl.DateTimeFormat(undefined, { timeZoneName: "short" }).formatToParts(
      new Date()
    );
    const tz = parts.find((p) => p.type === "timeZoneName");
    if (tz && tz.value) return tz.value;
  } catch {
    // fall through
  }
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "Local";
  } catch {
    return "Local";
  }
}

// Computed once at module load; the browser timezone does not change during a
// session.
const TZ_LABEL = localTimezoneLabel();

type PromoCodeEditorProps = {
  restaurantId: string;
};

type ItemSchemaGroupResponse = {
  item_schema_groups: Array<{
    item_schemas: Array<{ id: string; name: string }>;
  }>;
};

export const PromoCodeEditor: React.FC<PromoCodeEditorProps> = ({ restaurantId }) => {
  const { t } = useTranslation();
  const { token } = useAntdTheme();
  const { createSuccess, updateSuccess, notifyError } = useNotification();

  const [promoCodes, setPromoCodes] = useState<PromoCode[] | undefined>(undefined);
  const [itemSchemaOptions, setItemSchemaOptions] = useState<ItemSchemaOption[]>([]);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editingExpire, setEditingExpire] = useState<PromoCode | null>(null);
  const [createLoading, setCreateLoading] = useState(false);
  const [expireLoading, setExpireLoading] = useState(false);
  const [createForm] = Form.useForm();
  const [expireForm] = Form.useForm();

  const refreshPromoCodes = () => {
    getFetchT<PromoCode[]>(
      `${API_URL}/v1/r/${restaurantId}/admin/promo_codes`,
      (result) => {
        setPromoCodes(result ?? []);
      },
      (err) => {
        notifyError(t("failed_to_fetch_promo_codes"), err);
        setPromoCodes([]);
      }
    );
  };

  const refreshItemSchemas = () => {
    getFetchT<ItemSchemaGroupResponse>(
      `${API_URL}/v1/r/${restaurantId}/menu`,
      (data) => {
        const allItems = data.item_schema_groups.flatMap((g) => g.item_schemas);
        setItemSchemaOptions(allItems);
      },
      (err) => {
        notifyError(t("failed_to_fetch_item_schemas"), err);
      }
    );
  };

  useEffect(() => {
    refreshPromoCodes();
    refreshItemSchemas();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restaurantId]);

  const handleCreate = (values: {
    slug: string;
    description?: string;
    discount_type: DiscountType;
    discount_value: number;
    expire_time: Dayjs;
    min_spend_conditions?: Array<{ min_pre_tax_price: number }>;
    item_conditions?: Array<{
      item_schema_id: string;
      quantity: number;
      counts_towards_min_spend: boolean;
    }>;
  }) => {
    setCreateLoading(true);
    const req: CreatePromoCodeRequest = {
      slug: values.slug.trim(),
      description: values.description?.trim(),
      discount_type: values.discount_type,
      discount_value:
        values.discount_type === "PERCENT_OF_ORDER_TOTAL"
          ? Math.round(values.discount_value)
          : dollarToCents(values.discount_value),
      expire_time: values.expire_time.toISOString(),
      min_spend_conditions: (values.min_spend_conditions ?? []).map((m) => ({
        min_pre_tax_price: dollarToCents(m.min_pre_tax_price),
      })),
      item_conditions: (values.item_conditions ?? []).map((ic) => ({
        item_schema_id: ic.item_schema_id,
        quantity: ic.quantity,
        counts_towards_min_spend: ic.counts_towards_min_spend ?? false,
      })),
    };

    fetchT<PromoCode>(
      "POST",
      `${API_URL}/v1/r/${restaurantId}/admin/promo_codes`,
      req,
      () => {
        createSuccess(t("promo_code"));
        setCreateLoading(false);
        setCreateModalOpen(false);
        createForm.resetFields();
        refreshPromoCodes();
      },
      (err) => {
        notifyError(t("failed_to_create_promo_code"), err);
        setCreateLoading(false);
      }
    );
  };

  const handleUpdateExpire = (values: { expire_time: Dayjs }) => {
    if (!editingExpire) return;
    setExpireLoading(true);
    const req: UpdatePromoCodeRequest = {
      expire_time: values.expire_time.toISOString(),
    };
    fetchT<PromoCode>(
      "PUT",
      `${API_URL}/v1/r/${restaurantId}/admin/promo_codes/${editingExpire.id}`,
      req,
      () => {
        updateSuccess(t("promo_code"));
        setExpireLoading(false);
        setEditingExpire(null);
        expireForm.resetFields();
        refreshPromoCodes();
      },
      (err) => {
        notifyError(t("failed_to_update_promo_code"), err);
        setExpireLoading(false);
      }
    );
  };

  const formatDiscountValue = (code: PromoCode): string => {
    if (code.discount_type === "PERCENT_OF_ORDER_TOTAL") {
      return `${code.discount_value}%`;
    }
    return `$${centsToDollar(code.discount_value)}`;
  };

  const isExpired = (code: PromoCode): boolean => {
    return dayjs(code.expire_time).isBefore(dayjs());
  };

  const columns = [
    {
      title: t("promo_slug"),
      dataIndex: "slug",
      key: "slug",
      render: (slug: string, code: PromoCode) => (
        <Space>
          <Text strong>{slug}</Text>
          {isExpired(code) && <Tag color="default">{t("expired")}</Tag>}
        </Space>
      ),
    },
    {
      title: t("promo_description"),
      dataIndex: "description",
      key: "description",
      render: (d?: string) => d || <Text type="secondary">—</Text>,
    },
    {
      title: t("promo_discount"),
      key: "discount",
      render: (_: unknown, code: PromoCode) => formatDiscountValue(code),
    },
    {
      title: t("promo_conditions"),
      key: "conditions",
      render: (_: unknown, code: PromoCode) => {
        const parts: string[] = [];
        for (const m of code.min_spend_conditions ?? []) {
          parts.push(`${t("promo_min_spend")}: $${centsToDollar(m.min_pre_tax_price)}`);
        }
        for (const ic of code.item_conditions ?? []) {
          const item = itemSchemaOptions.find((i) => i.id === ic.item_schema_id);
          const name = item ? item.name : ic.item_schema_id.slice(0, 8);
          const suffix = ic.counts_towards_min_spend
            ? ""
            : ` (${t("promo_item_excludes_min_spend")})`;
          parts.push(`${t("promo_item")}: ${name} × ${ic.quantity}${suffix}`);
        }
        if (parts.length === 0) return <Text type="secondary">—</Text>;
        return (
          <Space direction="vertical" size={2}>
            {parts.map((p, i) => (
              <Text key={i} style={{ fontSize: token.fontSizeSM }}>
                {p}
              </Text>
            ))}
          </Space>
        );
      },
    },
    {
      title: `${t("promo_expire_time")} (${TZ_LABEL})`,
      dataIndex: "expire_time",
      key: "expire_time",
      render: (ts: string) => dayjs(ts).format("YYYY-MM-DD HH:mm"),
    },
    {
      title: t("actions"),
      key: "actions",
      render: (_: unknown, code: PromoCode) => (
        <Button
          size="small"
          icon={<EditOutlined />}
          onClick={() => {
            setEditingExpire(code);
            expireForm.setFieldsValue({
              expire_time: dayjs(code.expire_time),
            });
          }}
        >
          {t("edit_expire_time")}
        </Button>
      ),
    },
  ];

  if (promoCodes === undefined) {
    return <Spin size="large" />;
  }

  return (
    <div>
      <Card
        title={
          <Space>
            <GiftOutlined style={{ fontSize: 20, color: token.colorPrimary }} />
            <Text strong style={{ fontSize: token.fontSizeLG }}>
              {t("promo_codes")}
            </Text>
          </Space>
        }
        extra={
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              createForm.resetFields();
              createForm.setFieldsValue({
                discount_type: "PERCENT_OF_ORDER_TOTAL",
                min_spend_conditions: [{ min_pre_tax_price: 0 }],
              });
              setCreateModalOpen(true);
            }}
          >
            {t("create_promo_code")}
          </Button>
        }
        style={{
          borderRadius: token.borderRadiusLG,
          boxShadow: token.boxShadowSecondary,
          border: `1px solid ${token.colorBorder}`,
        }}
        styles={{ body: { padding: token.paddingLG } }}
      >
        {promoCodes.length === 0 ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={<Text type="secondary">{t("no_promo_codes_available")}</Text>}
          />
        ) : (
          <Table
            dataSource={promoCodes}
            columns={columns}
            rowKey="id"
            pagination={false}
            size="middle"
          />
        )}
      </Card>

      {/* Create modal */}
      <Modal
        title={t("create_promo_code")}
        open={createModalOpen}
        onCancel={() => setCreateModalOpen(false)}
        footer={null}
        width={640}
        destroyOnClose
      >
        <Form
          form={createForm}
          layout="vertical"
          onFinish={handleCreate}
          initialValues={{
            discount_type: "PERCENT_OF_ORDER_TOTAL",
            min_spend_conditions: [{ min_pre_tax_price: 0 }],
          }}
        >
          <Form.Item
            name="slug"
            label={t("promo_slug")}
            rules={[
              { required: true, message: t("slug_is_required") },
              { max: 100, message: t("slug_max_length") },
            ]}
          >
            <Input placeholder={t("slug_placeholder")} />
          </Form.Item>

          <Form.Item name="description" label={t("promo_description")}>
            <Input.TextArea rows={2} placeholder={t("description_placeholder")} />
          </Form.Item>

          <Space style={{ display: "flex", marginBottom: 8 }} align="start">
            <Form.Item
              name="discount_type"
              label={t("discount_type")}
              rules={[{ required: true }]}
              style={{ width: 220 }}
            >
              <Select
                options={[
                  { value: "PERCENT_OF_ORDER_TOTAL", label: t("percent_of_order_total") },
                  { value: "FIXED_AMOUNT", label: t("fixed_amount") },
                ]}
              />
            </Form.Item>
            <Form.Item
              shouldUpdate={(prev, cur) => prev.discount_type !== cur.discount_type}
              noStyle
            >
              {({ getFieldValue }) => {
                const dtype = getFieldValue("discount_type") as DiscountType;
                return (
                  <Form.Item
                    name="discount_value"
                    label={
                      dtype === "PERCENT_OF_ORDER_TOTAL"
                        ? t("discount_percent")
                        : t("discount_amount")
                    }
                    rules={[
                      { required: true, message: t("discount_value_is_required") },
                      {
                        validator: (_: unknown, value: number) => {
                          if (value == null) return Promise.resolve();
                          if (dtype === "PERCENT_OF_ORDER_TOTAL") {
                            if (value < 1 || value > 100) {
                              return Promise.reject(new Error(t("discount_percent_range")));
                            }
                          } else {
                            if (value <= 0) {
                              return Promise.reject(new Error(t("discount_amount_positive")));
                            }
                          }
                          return Promise.resolve();
                        },
                      },
                    ]}
                    style={{ width: 200 }}
                  >
                    <InputNumber
                      min={0}
                      precision={dtype === "PERCENT_OF_ORDER_TOTAL" ? 0 : 2}
                      style={{ width: "100%" }}
                      prefix={dtype === "FIXED_AMOUNT" ? "$" : undefined}
                      suffix={dtype === "PERCENT_OF_ORDER_TOTAL" ? "%" : undefined}
                    />
                  </Form.Item>
                );
              }}
            </Form.Item>
            <Form.Item
              name="expire_time"
              label={`${t("promo_expire_time")} (${TZ_LABEL})`}
              rules={[{ required: true, message: t("expire_time_is_required") }]}
              style={{ width: 220 }}
            >
              <DatePicker
                showTime
                style={{ width: "100%" }}
                disabledDate={(d) => d && d.isBefore(dayjs().startOf("day"))}
              />
            </Form.Item>
          </Space>

          {/* Min spend conditions — at most 1 */}
          <Form.List name="min_spend_conditions">
            {(fields, { add, remove }) => (
              <>
                <Text strong>{t("min_spend_conditions")}</Text>
                {fields.map(({ key, name, ...rest }) => (
                  <Space key={key} style={{ display: "flex", marginBottom: 8 }} align="baseline">
                    <Form.Item
                      {...rest}
                      name={[name, "min_pre_tax_price"]}
                      rules={[{ required: true, message: t("min_spend_is_required") }]}
                    >
                      <InputNumber min={0} precision={2} prefix="$" style={{ width: 180 }} />
                    </Form.Item>
                    <Button danger onClick={() => remove(name)}>
                      {t("delete")}
                    </Button>
                  </Space>
                ))}
                {fields.length === 0 && (
                  <Button
                    type="dashed"
                    onClick={() => add({ min_pre_tax_price: 0 })}
                    icon={<PlusOutlined />}
                  >
                    {t("add_min_spend_condition")}
                  </Button>
                )}
              </>
            )}
          </Form.List>

          {/* Item conditions */}
          <Form.List name="item_conditions">
            {(fields, { add, remove }) => (
              <>
                <div style={{ marginTop: 16 }}>
                  <Text strong>{t("item_conditions")}</Text>
                </div>
                {fields.map(({ key, name, ...rest }) => (
                  <div key={key} style={{ marginBottom: 8 }}>
                    <Space style={{ display: "flex", marginBottom: 8 }} align="baseline">
                      <Form.Item
                        {...rest}
                        name={[name, "item_schema_id"]}
                        rules={[{ required: true, message: t("item_is_required") }]}
                      >
                        <Select
                          showSearch
                          optionFilterProp="label"
                          placeholder={t("promo_select_item")}
                          style={{ width: 280 }}
                          options={itemSchemaOptions.map((i) => ({ value: i.id, label: i.name }))}
                        />
                      </Form.Item>
                      <Form.Item
                        {...rest}
                        name={[name, "quantity"]}
                        rules={[{ required: true, message: t("quantity_is_required") }]}
                      >
                        <InputNumber min={1} precision={0} style={{ width: 100 }} />
                      </Form.Item>
                      <Button danger onClick={() => remove(name)}>
                        {t("delete")}
                      </Button>
                    </Space>
                    <Form.Item
                      {...rest}
                      name={[name, "counts_towards_min_spend"]}
                      valuePropName="checked"
                      style={{ marginBottom: 0, marginLeft: 4 }}
                    >
                      <Switch
                        size="small"
                        checkedChildren={t("counts_towards_min_spend")}
                        unCheckedChildren={t("excludes_from_min_spend")}
                      />
                    </Form.Item>
                    <Text type="secondary" style={{ fontSize: token.fontSizeSM, marginLeft: 8 }}>
                      {t("promo_item_min_spend_help")}
                    </Text>
                  </div>
                ))}
                <Button
                  type="dashed"
                  onClick={() => add({ quantity: 1, counts_towards_min_spend: false })}
                  icon={<PlusOutlined />}
                >
                  {t("add_item_condition")}
                </Button>
              </>
            )}
          </Form.List>

          <Alert
            type="info"
            showIcon
            message={t("promo_code_condition_required_notice")}
            style={{ marginTop: 16, marginBottom: 16 }}
          />

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" loading={createLoading}>
                {t("create")}
              </Button>
              <Button onClick={() => setCreateModalOpen(false)}>{t("cancel")}</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Edit expire_time modal */}
      <Modal
        title={t("edit_expire_time")}
        open={editingExpire !== null}
        onCancel={() => setEditingExpire(null)}
        footer={null}
        destroyOnClose
      >
        {editingExpire && (
          <Form form={expireForm} layout="vertical" onFinish={handleUpdateExpire}>
            <Form.Item label={t("promo_slug")}>
              <Text strong>{editingExpire.slug}</Text>
            </Form.Item>
            <Form.Item
              name="expire_time"
              label={`${t("promo_expire_time")} (${TZ_LABEL})`}
              rules={[{ required: true, message: t("expire_time_is_required") }]}
            >
              <DatePicker showTime style={{ width: "100%" }} />
            </Form.Item>
            <Form.Item>
              <Space>
                <Button type="primary" htmlType="submit" loading={expireLoading}>
                  {t("save")}
                </Button>
                <Button onClick={() => setEditingExpire(null)}>{t("cancel")}</Button>
              </Space>
            </Form.Item>
          </Form>
        )}
      </Modal>
    </div>
  );
};
